import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { GroupedVirtuosoHandle } from 'react-virtuoso'
import { GroupedVirtuoso } from 'react-virtuoso'

import SearchGroupHeader from '~/components/search/SearchGroupHeader'

import type { CategoryTotals, FlatItem, ResultGroup, SearchCategory } from '../types'
import { isViewAllItem } from '../types'
import { QuickSearchResultItem, getActionLabel } from './QuickSearchResultItem'
import { QuickSearchViewAllItem } from './QuickSearchViewAllItem'

export type QuickSearchResultsListProps = {
  activeIndex: number
  groups: ResultGroup[]
  totals: CategoryTotals
  expandedCategories: Set<SearchCategory>
  isSingleCategory: boolean
  onSelect: (index: number) => void
  onCollapseCategory: (category: SearchCategory) => void
}

export function QuickSearchResultsList({
  activeIndex,
  groups,
  totals,
  expandedCategories,
  isSingleCategory,
  onSelect,
  onCollapseCategory
}: QuickSearchResultsListProps) {
  const t = useTranslations('quickSearch')
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null)
  const activeItemRef = useRef<HTMLLIElement | null>(null)

  // Workaround for react-virtuoso issue #319:
  // Filter out empty groups to prevent phantom item renders and index offsets.
  // Include loading groups so their headers (with spinners) still render.
  const renderGroups = useMemo(() => groups.filter((g) => g.isLoading || g.items.length > 0), [groups])
  const renderGroupCounts = useMemo(() => renderGroups.map((g) => g.items.length), [renderGroups])

  const categoryLabel = useCallback(
    (category: SearchCategory) => {
      switch (category) {
        case 'charger':
          return t('categories.chargers')
        case 'site':
          return t('categories.sites')
        case 'vehicle':
          return t('categories.vehicles')
        case 'user':
          return t('categories.users')
        case 'org':
          return t('categories.organizations')
        case 'synoplink':
          return t('categories.synopLinks')
        case 'solarArray':
          return t('categories.solarArrays')
        case 'page':
          return t('categories.pages')
      }
    },
    [t]
  )

  // Flatten groups into a single array for item rendering
  const flatItems = useMemo(() => {
    const items: { item: FlatItem; flatIndex: number }[] = []
    let flatIndex = 0

    renderGroups.forEach((group) => {
      group.items.forEach((item) => {
        items.push({ item, flatIndex })
        flatIndex++
      })
    })

    return items
  }, [renderGroups])

  // Scroll active item into view when activeIndex changes
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < flatItems.length) {
      virtuosoRef.current?.scrollToIndex({
        index: activeIndex,
        behavior: 'smooth',
        align: 'center'
      })
    }
  }, [activeIndex, flatItems.length])

  const renderGroupHeader = (groupIndex: number) => {
    const group = renderGroups[groupIndex]
    if (!group) return null

    const isExpanded = expandedCategories.has(group.category)
    const canCollapse = isExpanded && !isSingleCategory && !group.isLoading

    return (
      <SearchGroupHeader
        label={categoryLabel(group.category)}
        isExpanded={isExpanded}
        isLoading={group.isLoading}
        resultCount={totals[group.category]}
        collapseLabel={t('actions.showLess')}
        onCollapse={canCollapse ? () => onCollapseCategory(group.category) : undefined}
      />
    )
  }

  const renderItem = (itemIndex: number) => {
    const entry = flatItems[itemIndex]
    if (!entry) return null

    const { item, flatIndex } = entry

    if (isViewAllItem(item)) {
      return (
        <QuickSearchViewAllItem
          ref={flatIndex === activeIndex ? activeItemRef : null}
          category={item.category}
          hiddenCount={item.hiddenCount}
          selected={flatIndex === activeIndex}
          onSelect={() => onSelect(flatIndex)}
        />
      )
    }

    return (
      <QuickSearchResultItem
        ref={flatIndex === activeIndex ? activeItemRef : null}
        result={item}
        selected={flatIndex === activeIndex}
        actionLabel={getActionLabel(item.category, t)}
        onSelect={() => onSelect(flatIndex)}
      />
    )
  }

  return (
    <GroupedVirtuoso
      ref={virtuosoRef}
      groupCounts={renderGroupCounts}
      overscan={200}
      style={{ height: '60vh' }}
      groupContent={renderGroupHeader}
      itemContent={renderItem}
    />
  )
}
